import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  output,
  inject,
  input,
} from '@angular/core';

import type { User } from '@core/account-types';
import { NotificationsAnalytics } from '@core/analytics-legacy';
import { AnalyticsMode } from '@core/analytics-types';
import { I18nService } from '@core/i18n-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  GroupComponent,
  IconComponent,
  OverlayOriginDirective,
  RtStatefulComponent,
  RtTooltip,
} from '@core/user-interface-legacy';

import { TotalFollowersComponent } from '../total-followers/total-followers.component';

type State = {
  title: string;
  description: string;
  dropdownOpen: boolean;
};

@Component({
  selector: 'rt-follow-button',
  templateUrl: './follow-button.component.html',
  styleUrls: ['./follow-button.component.less'],
  providers: [NotificationsAnalytics],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    GroupComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    OverlayOriginDirective,
    IconComponent,
    TotalFollowersComponent,
    RtTooltip,
  ],
})
export class FollowButtonComponent extends RtStatefulComponent<State> {
  private readonly i18n = inject(I18nService);
  private readonly analytics = inject(NotificationsAnalytics);
  protected readonly cdRef: ChangeDetectorRef;

  public readonly type = input<string>(undefined!);
  public readonly followed = input<boolean>(undefined!);
  public readonly kind = input<string>(undefined!);
  public readonly loading = input<boolean>(false);
  public readonly responsive = input<boolean>(false);
  @Input() public users: User[] = [];

  public followClick = output<void>();
  public unfollowClick = output<void>();

  @ViewChild(RtTooltip)
  public tooltip?: RtTooltip;

  public tooltipText: string | null;

  public statusText: string;

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      title: '',
      description: '',
      dropdownOpen: false,
    });
  }

  public ngOnInit(): void {
    const type = this.type();
    if (type === 'candidate' || type === 'requisition') {
      this.setState({
        ...this.state,
        title: this.i18n.translate(`app.follow_dropdown.${type}_title`),
        description: this.i18n.translate(`app.follow_dropdown.${type}_description`),
      });
    } else {
      this.setState({
        ...this.state,
        title: this.i18n.translate(`app.follow_dropdown.${type}_title_${this.kind()}`),
        description: this.i18n.translate(`app.follow_dropdown.${type}_description_${this.kind()}`),
      });
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.loading || changes.followed) {
      if (this.state.dropdownOpen) {
        this.setState(
          {
            ...this.state,
            dropdownOpen: false,
          },
          false,
        );
      }
    }

    const type = this.type();
    if (type === 'candidate' || type === 'requisition') {
      this.tooltipText = this.followed()
        ? this.i18n.translate(`app.follow_dropdown.unfollow_btn_tooltip_${type}`)
        : this.i18n.translate(`app.follow_dropdown.follow_btn_tooltip_${type}`);
    } else if (type === 'offer') {
      this.tooltipText = this.followed()
        ? this.i18n.translate(
            this.kind() === 'talent_pool'
              ? 'app.follow_dropdown.unfollow_btn_tooltip_talent_pool'
              : 'app.follow_dropdown.unfollow_btn_tooltip_job',
          )
        : this.i18n.translate(
            this.kind() === 'talent_pool'
              ? 'app.follow_dropdown.follow_btn_tooltip_talent_pool'
              : 'app.follow_dropdown.follow_btn_tooltip_job',
          );
    }

    this.setStatusText();
  }

  public followAction(): void {
    this.followClick.emit();
    const type = this.type();
    this.analytics.trackFollowAction(type === 'offer' ? this.kind() : type, AnalyticsMode.Single);
  }

  public unfollowAction(): void {
    this.unfollowClick.emit();
    const type = this.type();
    this.analytics.trackUnfollowAction(type === 'offer' ? this.kind() : type, AnalyticsMode.Single);
  }

  public toggleDropdown(state: boolean | undefined = undefined): void {
    this.setState({
      ...this.state,
      dropdownOpen: state === undefined ? !this.state.dropdownOpen : state,
    });
  }

  public trackOpenSettings(): void {
    this.analytics.trackOpenSettings();
  }

  public hideTooltip(tooltip?: RtTooltip): void {
    tooltip?.hideSubject.next({
      ...tooltip?.hideSubject.value,
      eventEmitted: true,
    });
  }

  public showTooltip(tooltip?: RtTooltip): void {
    tooltip?.showSubject?.next({ delay: 0 });
  }

  private setStatusText(): void {
    this.statusText = this.followed()
      ? this.i18n.translate('app.follow_dropdown.following')
      : this.i18n.translate('app.follow_dropdown.follow');
  }
}
